import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  CameraThumbnailsData,
  ThumbOptions,
} from '../../../../cameras/camera-thumbnails/camera-thumbnails.model';
import { MultiPlaybackData } from '@models/multi-playback.model';
import { MultiPlaybackActions } from '@states/multi-playback/multi-playback.action-types';
import {
  ThumbnailDialogData,
  ThumbnailDialogType,
} from '../../../../shared/thumbnail/thumbnail-dialog/thumbnail-dialog.component';
import { filter, from, map, switchMap, take } from 'rxjs';
import { GrantedAccessActions } from '@states/granted-access/granted-access.action-types';
import * as EdgeEditActions from '@states/edge-edit/edge-edit.actions';
import { ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import * as GrantedAccessSelectors from '@states/granted-access/granted-access.selectors';
import { AppState } from '../../../../store/app.state';
import { CamerasThumbnailsService } from '../../../../cameras/camera-thumbnails/camera-thumnails.service';
import { EdgeEditEffects } from '@effects/edge-edit.effects';
import { VideoService } from '../../../../development/video.service';
import { SharedApiService } from '../../../../services/shared-api.service';
import { ThumbnailsService } from '../../../../development/thumbnails.service';
import { ControlEvent } from '@models/control-event.model';
import AccessControlEventDetails = ControlEvent.AccessControlEventDetails;
import { LetDirective } from '@ngrx/component';
import { SharedModule } from '../../../../shared/shared.module';
import { UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-shared-event',
  imports: [LetDirective, SharedModule, UiButtonComponent],
  templateUrl: './shared-event.component.html',
  styleUrl: './shared-event.component.scss',
})
export class SharedEventComponent implements OnInit {
  public selectSharedEvent = this.store$.pipe(select(GrantedAccessSelectors.selectSharedEvent));
  public dialogData: ThumbnailDialogData;

  public online: boolean = false;
  private edgeId: string;
  private cameraId: string;

  constructor(
    private store$: Store<AppState>,
    private cameraThumbnailsService: CamerasThumbnailsService,
    private editEdgeEffects: EdgeEditEffects,
    private videoService: VideoService,
    private sharedApiService: SharedApiService,
    private thumbnailsService: ThumbnailsService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.videoService.setLive(false);
    this.selectSharedEvent
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => !!event),
        take(1),
        switchMap((event) => {
          const edgeId = event.camerasListDetails[0].edgeId;
          this.cameraId = event.camerasListDetails[0].cameraId;
          this.edgeId = edgeId;

          // this.store$.dispatch(EdgeActions.getEdgesLastMp4Ts());
          this.store$.dispatch(GrantedAccessActions.getCameraAudio({ edgeId: this.edgeId, cameraId: this.cameraId }));
          this.store$.dispatch(GrantedAccessActions.getEdgeLastMp4({ edgeId }));
          this.store$.dispatch(EdgeEditActions.setSelectedEdgeId({ selectedEdgeId: edgeId }));

          return from(this.thumbnailsService.getGcpTransitionForEdge(edgeId)).pipe(map(() => event));
        }),
      )
      .subscribe((event) => {
        this.loadPlayback(event);
      });

    this.editEdgeEffects.getEdgeHeartBeatStatus$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(GrantedAccessActions.getEdgeHeartBeatStatusSuccess),
        map((res) => res.body),
      )
      .subscribe((edgeStatus) => {
        const item = edgeStatus.find(
          (entry) => entry.component === 9 && entry.edgeId === this.edgeId,
        );
        this.online = item?.status === 0;
      });
  }

  public openMail() {
    window.open(`mailto:sales@lumana.ai`);
  }

  public loadPlayback(event: AccessControlEventDetails): void {
    //todo move to effect
    this.sharedApiService.getLocationTimezoneByEdgeId(this.edgeId).subscribe(async (res) => {
      const timezone = res.timezone;
      const startTime = this.normalizeTimestamp(event.timestamp - 40000);
      const endTime = this.normalizeTimestamp(Math.min(event.timestamp + 40000, new Date().getTime()));

      this.videoService.timeLine = { start: startTime, end: endTime };

      const options: ThumbOptions = {
        clipInSeconds: 7200,
        offsetResInDurations: 1,
        startTime,
        endTime,
        duration: 2000,
      };

      const thumbsData: CameraThumbnailsData = {
        edgeId: this.edgeId,
        cameraId: this.cameraId,
        timezone: timezone,
        offsetResInDurations: 1,
      };

      const multiPlaybackData: MultiPlaybackData = {
        base: startTime,
        duration: 2000,
        endTime,
        offsetResInDurations: 1,
        startTime,
        timezone,
      };
      this.store$.dispatch(MultiPlaybackActions.setData({ data: multiPlaybackData }));

      this.cameraThumbnailsService.setThumbnailsData(thumbsData);

      this.dialogData = {
        options,
        events: null,
        edgeId: this.edgeId,
        cameraId: this.cameraId,
        controlEventOptions: {
          eventId: event.id,
          openInfoTab: false,
        },
        selectedCamera: {
          cameraId: this.cameraId,
          edgeId: this.edgeId,
          // locationId: '',
          timezone: timezone,
        },
        objects: [],
        type: ThumbnailDialogType.ACCESS_CONTROL_EVENTS,
        rtl: false,
        defaultThumb: '',
        showObjects: false,
        seconds: true,
        allowTrack: false,
        alertName: event.eventName,
        name: 'CONTROL_EVENTS.INFO.EVENT_DETAILS',
      };
    });
  }

  private normalizeTimestamp(timestamp: number) {
    return this.cameraThumbnailsService.normalizeTimestamp(timestamp);
  }
}
